<template>
    <div :style="{ overflow }" class="panel is--extractor">
        <div ref="wrapping-container" class="is--playground-image">
            <playground v-if="showPlayground"
                        @updateHandle="updateHandle"
                        :viewport="viewport"
                        :coords="config.coords"
                        :options="options"
                        :shape="shapes[selectedIndex]"
                        :images="config.images"
                        :selectedImage="selectedImage"
            >
                <template slot="bottom">
                    <div class="has--playground-actions">
                        <button @click="addExtractionAndClose"
                            v-if="config.editInPlace"
                            :disabled="isLoadingActive"
                            :class="[ { 'is--loading': isLoadingActive }, 'btn', 'is--primary', 'is--center' ]"
                        >
                            <template v-if="0 === this.config.images[this.selectedImage].completeTimestamp">{{ $t('extractor.fileInProgress') }}</template>
                            <template v-else>{{ $t('extractor.modifyExtraction') }}</template>
                        </button>

                        <template v-if="! config.editInPlace">
                            <button :class="[ { 'is--loading': isLoadingActive }, 'btn', 'is--third', 'is--center', 'is--small' ]"
                                @click="addExtractionAndNext"
                                v-if="(selectedImage + 1) < config.images.length"
                                :disabled="isLoadingActive"
                            >
                                <template v-if="0 === this.config.images[this.selectedImage].completeTimestamp">{{ $t('extractor.fileInProgress') }}</template>
                                <template v-else>{{ $t('extractor.addExtractionAndNext') }}</template>
                            </button>

                            <button :class="[ { 'is--loading': isLoadingActive }, 'btn', 'is--primary', 'is--center' ]"
                                @click="addExtractionAndClose"
                                v-if="(selectedImage + 1) === config.images.length"
                                :disabled="isLoadingActive"
                            >
                                <template v-if="0 === this.config.images[this.selectedImage].completeTimestamp">{{ $t('extractor.fileInProgress') }}</template>
                                <template v-else>{{ $t('extractor.addExtractionAndClose') }}</template>
                            </button>
                        </template>
                    </div>
                </template>
            </playground>
        </div>
    </div>
</template>

<script>
    import EventBus from '../event-bus';
    import ShapesConfiguration from '../shapes-configuration';
    import Playground from './ClipPath/Playground';
    import Offcanvas from './Offcanvas';
    import NProgress from 'nprogress';
    import { clipPoints, calcMaxDimensions, generateRandomID, removeURIParams } from '../utilities';

    export default {
        data() {
            let orientation = this.viewport.width < this.viewport.height ? 'portrait' : 'landscape';

            return {
                showPlayground: false,
                selectedImage: 0,
                isLoading: false,

                orientation,

                options: {
                    width: 640,
                    height: 480,
                    maxWidth: 640,
                    maxHeight: 480,
                    shadowBoard: true
                },

                selectedIndex: 0,
                shapes: ShapesConfiguration,
                offcanvasMenuIsOpen: false,
                resizeOffsetTimeout: false,
                timeoutResizeFunction: false,
                offset: {
                    x: 20,
                    y: 0,
                },
                handlesInitialized: false,
                handlesMoved: false,
                warningOpened: false,
            };
        },

        props: {
            viewport: Object,
            config: Object,
            availableHeight: Number,
        },

        computed: {
            insufficientQuality() {
                let undef;

                if (undef === this.config.images[this.selectedImage].info) {
                    return false;
                }

                return this.config.images[this.selectedImage].info['insufficient-quality'];
            },

            isLoadingActive() {
                return 0 === this.config.images[this.selectedImage].completeTimestamp || this.isLoading;
            },
        },

        methods: {
            handlesReady() {
                this.handlesInitialized = true;
                this.handlesMoved = false;
            },

            updateHandle(payload, percental) {
                percental = percental || false;

                let x = payload.x,
                    y = payload.y;

                if (! percental) {
                    x = Math.round(x / this.options.width * 100);
                    y = Math.round(y / this.options.height * 100);
                }

                if (this.handlesInitialized) {
                    this.handlesMoved = true;
                }

                this.$set(this.config.coords, payload.i, [x, y]);
            },

            openOffcanvasMenu() {
                this.offcanvasMenuIsOpen = true;
            },

            closeOffcanvasMenu() {
                this.offcanvasMenuIsOpen = false;
            },

            showHandlesNotMovedWarning(callback = () => {}) {
                let vm = this;

                if (
                    vm.handlesInitialized
                    && ! vm.handlesMoved
                    && ! vm.warningOpened
                ) {
                    vm.warningOpened = true;

                    vm.$modal.show('dialog', {
                        title: vm._i18n.t('hint.handles.title'),
                        text: vm._i18n.t('hint.handles.text'),
                        buttons: [
                            {
                                title: vm._i18n.t('hint.handles.button.left'),
                                handler: () => {
                                    // console.log(callback);
                                    callback();
                                    this.$modal.hide('dialog')
                                },
                                class: 'vue-dialog-button is--error',
                            },
                            {
                                title: vm._i18n.t('hint.handles.button.right'),
                                handler: () => {
                                    vm.warningOpened = false;
                                    this.$modal.hide('dialog')
                                },
                                class: 'vue-dialog-button is--success',
                            },
                        ]
                    });

                    return true;
                }

                return false;
            },

            addExtractionAndNext(event) {
                let vm = this;

                if (
                    ! this.config.editInPlace
                    && this.showHandlesNotMovedWarning(() => { vm.addExtractionAndNext(event); })
                ) {
                    return;
                }

                this.addExtraction(event, extraction => {
                    vm.$emit('addToEditor', extraction, false);
                    vm.next();

                    return false;
                });
            },

            addExtractionAndClose(event) {
                let vm = this;

                if (
                    ! this.config.editInPlace
                    && this.showHandlesNotMovedWarning(() => { vm.addExtractionAndClose(event); })
                ) {
                    return;
                }

                this.addExtraction(event, extraction => {
                    vm.$emit('addToEditor', extraction, false);
                    vm.close(true);

                    return false;
                });
            },

            addExtraction(event, callback) {
                this.isLoading = true;

                callback = callback || (() => {});

                let vm     = this,
                    coords = clipPoints(vm.config.coords),
                    parent = vm.config.images[vm.selectedImage];

                NProgress.start();

                vm.$http.post(App.url.clip, {
                    source: removeURIParams(parent.source),
                    coords: coords
                }).then(
                    // Successful
                    response => {
                        vm.isLoading = false;

                        let extraction = {
                            parent: parent,
                            geometry: response.data.geometry,
                            source: response.data.pathToFile,
                            coords: JSON.parse(JSON.stringify(vm.config.coords)),
                            inUse: false,
                            uuid: generateRandomID()
                        };

                        // TODO: Evaluate loading image dimensions by php
                        vm.$emit('addExtraction', extraction);

                        NProgress.done();
                        window.setTimeout(function() {
                            NProgress.remove();
                            EventBus.$emit('Handles:reset');
                        }, 500);

                        if (vm.config.editInPlace) {
                            vm.$emit('addToEditor', extraction, false, vm.config.target);
                            vm.close(true);

                            return;
                        }

                        if (false === callback.call(vm, extraction)) {
                            return;
                        }

                        if (vm.offcanvasMenuIsOpen) {
                            return;
                        }

                        vm.openOffcanvasMenu();

                        window.setTimeout(() => {
                            vm.closeOffcanvasMenu();
                        }, 1000);
                    },
                    // Errors occurs
                    response => {
                        // alert('Kapott');
                        NProgress.remove();
                    }
                );
            },

            loadCoords(extraction) {
                this.config.coords = JSON.parse(JSON.stringify(extraction.coords));
                EventBus.$emit('Handles:reset', this.config.coords);
            },

            previous() {
                if (1 > this.selectedImage) {
                    return;
                }

                this.selectedImage = this.selectedImage - 1;
                this.calculatePlaygroundDimensions();
                EventBus.$emit('Handles:reset')
            },

            next() {
                if (this.selectedImage >= this.config.images.length - 1) {
                    return;
                }

                this.handlesMoved  = false;
                this.warningOpened = false;

                this.selectedImage = this.selectedImage + 1;
                this.calculatePlaygroundDimensions();
                EventBus.$emit('Handles:reset')
            },

            resetHandles() {
                EventBus.$emit('Handles:reset');
            },

            close(openEditor) {
                EventBus.$emit('Handles:reset');
                this.$emit('close', openEditor);
            },

            calculatePlaygroundDimensions() {
                let vm = this, image = new Image();

                image.addEventListener('load', function() {
                    let dim = calcMaxDimensions(
                        image.width,
                        image.height,
                        vm.options.maxWidth,
                        vm.options.maxHeight
                    );

                    // console.log({
                    //     'image.width': image.width,
                    //     'image.height': image.height,
                    //     'vm.options.maxWidth': vm.options.maxWidth,
                    //     'vm.options.maxHeight': vm.options.maxHeight,
                    // });

                    // console.log(dim);

                    vm.options.width  = dim.x;
                    vm.options.height = dim.y;

                    vm.showPlayground = true;
                });

                // console.log(vm.config.images[this.selectedImage].source);

                image.src = vm.config.images[this.selectedImage].source;
            },

            viewportResized() {
                this.options.width     = this.$refs['wrapping-container'].offsetWidth - 20 - (2 * this.offset.x);
                this.options.maxWidth  = this.options.width;
                this.options.maxHeight = this.availableHeight - 160;

                this.calculatePlaygroundDimensions();
            },

            registerIntroJsStep(config) {
                if ('clipping' !== config.view) {
                    return;
                }

                config.instance.push({
                    element: this.$refs['wrapping-container'].querySelector('.sandbox'),
                    position: 'top-middle-aligned',
                    intro: 'Markiere mit den blauen Anfassern nur <span>grob den Bereich</span>, den Du freigestellt\n' +
                        'bekommen möchtest. Die <span>exakte Entfernung</span> des Hintergrund übernehmen\n' +
                        'wir mit unseren erfahrenen <span>Grafikern</span>.',
                    step: 10,
                });
            },
        },

        created() {
            let vm = this, newOrientation;

            vm.timeoutResizeFunction = (values) => {
                newOrientation = vm.viewport.width < vm.viewport.height ? 'portrait' : 'landscape';

                if (vm.orientation === newOrientation) {
                    return;
                }

                vm.orientation = newOrientation;

                if (false !== vm.resizeOffsetTimeout) {
                    window.clearTimeout(vm.resizeOffsetTimeout);
                }

                vm.resizeOffsetTimeout = window.setTimeout(() => {
                    vm.viewportResized.call(vm, values);
                }, 500);
            };

            EventBus.$on('resized', vm.timeoutResizeFunction);
            EventBus.$on('collectIntroJsSteps', this.registerIntroJsStep);
        },

        mounted() {
            EventBus.$on('ClipPath:handlesReady', this.handlesReady);
            this.viewportResized();

            EventBus.$emit('startIntroDelayedBy', 'clipping');
        },

        beforeDestroy() {
            EventBus.$off('resized', this.timeoutResizeFunction);
            EventBus.$off('ClipPath:handlesReady', this.handlesReady);
            EventBus.$off('collectIntroJsSteps', this.registerIntroJsStep);
        },

        components: {
            Playground,
            Offcanvas
        }
    }
</script>

<style lang="scss">
    @import "../assets/scss/utilities";

    main {
        display: flex;
        flex-direction: column;

        @media (min-width: 800px) {
            position: fixed;
            top: 0;
            left: 0;
            height: 100%;
            right: 23.625rem;
            padding: .25rem .25rem .25rem .75rem;
            touch-action: none;

            .panel.dark {
                display: none;
            }

        }
    }

    .v--modal-overlay {
        background: rgba(0, 0, 0, 0.8);
    }

    .v--modal-top-right {
        z-index: 9999;
    }
</style>

<style scoped lang="scss">
    @import "../assets/scss/components/clip-path";

    .ct-top-right {
        cursor: pointer;
        padding-top: 20px;
        padding-right: 30px;
        font-weight: 600;
        color: white;
    }
    .scale-enter-active,
    .scale-leave-active {
        transition: all 0.5s;
    }
    .scale-enter,
    .scale-leave-active {
        opacity: 0;
        transform: scale(0.3) translateY(24px);
    }
</style>



// WEBPACK FOOTER //
// src/components/ClipPath.vue